import type { AxiosResponse } from 'axios';

import { api, api2, ttsApi } from 'src/utils/axios';
import { DEFAULT_DATASET_TRAIN_SET_PHRASES_COUNT } from 'src/constants';
import logger from 'src/lib/logger';
import type {
  AddCustomPhraseResponse,
  CasrScenarioStatsResponse,
  Consents,
  DatasetTrainingDatasetResponse,
  DeleteAccountParams,
  FeatureFlags,
  FetchNotesPaginatedParams,
  GetPhrasesBySourceParams,
  ImportRecordingMetadata,
  ModelInfo,
  ModelLinks,
  Note,
  Phrase,
  ProfileResponse,
  ProfileStatusResponse,
  RecordingInfo,
  RequestSynthesisParams,
  Scenario,
  ShortcutPhrase,
  SocialConnection,
  SubscriptionCheckoutStatusResponse,
  SubscriptionInfoResponse,
  SubscriptionLinkResponse,
  SupportContact,
  TalkToBotResponse,
  TechPartnersResponse,
  UpdateConsentsParams,
  UpdateProfileParams,
  UserSettings,
  Voice,
} from './types';

class ClientApi {
  async getModelsInfo(): Promise<ModelInfo[] | undefined> {
    const { data } = await api.get(`/app/modelsInfo`);
    return data?.models;
  }

  async updateProfile({
    name,
    gender,
    country,
    diagnosis,
    speechLevel,
    nativeLanguage,
    phone,
    region_code,
    role,
    referral_source,
  }: UpdateProfileParams): Promise<AxiosResponse<unknown>> {
    const payload: UpdateProfileParams = {
      name,
      gender,
      country,
      diagnosis,
      speechLevel,
      nativeLanguage,
      phone,
      region_code,
      role,
      referral_source,
    };
    return api.post('/app/profile', payload);
  }

  // Secondary ("support") contact — separate endpoint from the main profile.
  // TODO: confirm path + payload field names with backend.
  async getSupportContact(): Promise<SupportContact> {
    const { data } = await api2.get('/app/end_user/support_contact');
    return data;
  }

  async updateSupportContact({
    name,
    email,
    phone,
  }: SupportContact): Promise<SupportContact> {
    const payload: SupportContact = { name, email, phone };
    const { data } = await api2.post('/app/end_user/support_contact', payload);
    return data;
  }

  async deleteMyself({
    password,
    opt_out_audio_recognition,
    opt_out_audio_training,
  }: DeleteAccountParams): Promise<AxiosResponse<unknown>> {
    return await api2.delete(`/app/end_user/delete_account`, {
      data: {
        password,
        opt_out_audio_recognition,
        opt_out_audio_training,
      },
    });
  }

  async getCasrScenarioStats(
    provider: string,
  ): Promise<CasrScenarioStatsResponse> {
    const { data } = await api.get(`/app/casr/scenarios_stats`, {
      params: { provider },
    });
    return data;
  }

  async getDatasetTrainingDataset(
    scenarioId: string,
    limit: number = DEFAULT_DATASET_TRAIN_SET_PHRASES_COUNT,
  ): Promise<DatasetTrainingDatasetResponse> {
    const { data } = await api.get(
      `/app/casr/scenario_train_set/${scenarioId}/dataset`,
      {
        params: {
          limit,
        },
      },
    );
    return data;
  }

  async getPhrasesBySource({
    provider,
    scenarioId,
    source,
  }: GetPhrasesBySourceParams): Promise<Phrase[]> {
    const { data } = await api.get(`/app/casr/phrases`, {
      params: { provider, scenario_id: scenarioId, sources: source },
    });
    return data;
  }

  async addDatasetPhrase(
    scenarioId: string,
    phraseId: string,
  ): Promise<Phrase> {
    const { data } = await api.post('/app/casr/datasetPhrase', {
      id: phraseId,
      scenario_id: scenarioId,
    });
    return data;
  }

  async addCustomPhrase(
    text: string,
    language: string,
    scenarioId: string,
  ): Promise<AddCustomPhraseResponse> {
    const { data } = await api.post('/app/casr/customPhrase', {
      text,
      language,
      scenario_id: scenarioId,
    });
    return data;
  }

  async getPhrase(phraseId: string): Promise<Phrase> {
    const { data } = await api.get(`/app/casr/phrase/${phraseId}`);
    return data;
  }

  async deletePhrase(phraseId: string): Promise<unknown> {
    const { data } = await api.post('/app/casr/deletePhrase', {
      phrase_id: phraseId,
    });
    return data;
  }

  async editPhrase(phraseId: string, newText: string): Promise<unknown> {
    const { data } = await api.put('/app/casr/editPhrase', {
      phrase_id: phraseId,
      text: newText,
    });
    return data;
  }

  async getScenarios(): Promise<Scenario[]> {
    const { data } = await api.get('/app/scenarios');
    return data.availableScenarios;
  }

  async activateScenario(id: string): Promise<AxiosResponse<unknown>> {
    return await api.put(`/app/scenarios/activate/${id}`);
  }

  async uploadExemplar(
    phraseId: string,
    audio: Blob,
    metadata: RecordingInfo,
    fileName: string,
    fileExtension: string,
  ): Promise<AxiosResponse<unknown>> {
    const formData = new FormData();
    formData.append(
      'info',
      new Blob([JSON.stringify(metadata)], { type: 'application/json' }),
      `${fileName}.json`,
    );
    formData.append('audiofile', audio, `${fileName}.${fileExtension}`);

    return api.post(`app/casr/upload/training/${phraseId}`, formData);
  }

  async uploadRecognition(
    audio: Blob,
    info: RecordingInfo,
    fileName: string,
  ): Promise<AxiosResponse<unknown>> {
    const formData = new FormData();
    formData.append(
      'info',
      new Blob([JSON.stringify(info)], { type: 'application/json' }),
      `${fileName}.json`,
    );
    formData.append('audio', audio, `${fileName}.${info.file_ext}`);

    return api2.post(`/app/recording/recognition`, formData);
  }

  async importRecording(
    metadata: ImportRecordingMetadata,
    audioBlob: Blob,
    audioFilename: string,
  ): Promise<AxiosResponse<unknown>> {
    const formData = new FormData();
    formData.append(
      'info',
      new Blob([JSON.stringify(metadata)], { type: 'application/json' }),
      `${audioFilename.split('.')[0]}.json`,
    );
    formData.append('audiofile', audioBlob, audioFilename);
    return api.post(`app/casr/import/training`, formData);
  }

  async resetAlbCookies(): Promise<AxiosResponse<unknown>> {
    const location = window.location.protocol + '//' + window.location.host;
    return api.get(`${location}/resetCookies`, { withCredentials: true });
  }

  async getProfile(): Promise<ProfileResponse> {
    const { data } = await api.get('/app/profile');
    return data;
  }

  async getProfileStatus(): Promise<ProfileStatusResponse> {
    const { data } = await api.get('/app/profile/status');
    return data;
  }

  async updateUserSettings(settings: UserSettings): Promise<UserSettings> {
    const { data } = await api.post('/app/updateUserSettings', settings);
    return data;
  }

  async getUserSettings(): Promise<UserSettings> {
    const { data } = await api.get('/app/getUserSettings');
    return data;
  }

  async fetchUserTasks(): Promise<string[] | undefined> {
    const { data } = await api.get('/app/tasks');
    return data?.tasks;
  }

  async completeTask(task: string): Promise<unknown> {
    const { data } = await api.post('/app/complete_task', { task });
    return data;
  }

  async requestSupport(
    type: string,
    text: string,
    metadata: unknown,
  ): Promise<unknown> {
    const { data } = await api.post('/app/support', { type, text, metadata });
    return data;
  }

  async talkToBot(text: string): Promise<TalkToBotResponse> {
    const { data } = await api2.post('/app/chatgpt/chat', { text });
    return data;
  }

  async fetchShortcutPhrases(): Promise<ShortcutPhrase[]> {
    const { data } = await api.get('/app/casr/shortcut_phrases');
    return data;
  }

  async addShortcutPhrase(
    input: string,
    output: string,
  ): Promise<ShortcutPhrase> {
    const { data } = await api.post('/app/casr/shortcut_phrases', {
      input,
      output,
    });
    return data;
  }

  async fetchShortcutPhrase(id: string): Promise<ShortcutPhrase> {
    const { data } = await api.get(`/app/casr/shortcut_phrases/${id}`);
    return data;
  }

  async editShortcutPhrase(
    id: string,
    input: string,
    output: string,
  ): Promise<ShortcutPhrase> {
    const { data } = await api.put(`/app/casr/shortcut_phrases/${id}`, {
      input,
      output,
    });
    return data;
  }

  async deleteShortcutPhrase(id: string): Promise<unknown> {
    const { data } = await api.delete(`/app/casr/shortcut_phrases/${id}`);
    return data;
  }

  async fetchNotesPaginated({
    perPage,
    page,
  }: FetchNotesPaginatedParams): Promise<Note[]> {
    const { data } = await api2.get(`/app/dictation_notes`, {
      params: { perPage, page },
    });
    if (!Array.isArray(data)) {
      logger.error(
        `fetchNotesPaginated: expected array, got ${typeof data}: ${String(data).slice(0, 300)}`,
      );
      return [];
    }
    return data;
  }

  async addNote(text: string): Promise<Note> {
    const { data } = await api2.post('/app/dictation_notes', { text });
    return data;
  }

  async editNote(id: string, text: string): Promise<Note> {
    const { data } = await api2.patch(`/app/dictation_notes/${id}`, { text });
    return data;
  }

  async deleteNote(id: string): Promise<unknown> {
    const { data } = await api2.delete(`/app/dictation_notes/${id}`);
    return data;
  }

  async fetchSocialConnections(): Promise<SocialConnection[]> {
    const { data } = await api2.get(`/app/auth/linked_social_accounts`);
    return data;
  }

  async fetchFeatureFlags(): Promise<FeatureFlags> {
    const { data } = await api2.get(`/app/public/feature_flags`);
    return data;
  }

  async fetchSubscriptionInfo(): Promise<SubscriptionInfoResponse | undefined> {
    const { data } = await api2.get(`/app/end_user/subscription_info`);
    if (!!data) {
      return data;
    }
  }

  async fetchSubscriptionOptions(): Promise<unknown> {
    const { data } = await api2.get(`/app/end_user/subscription/options`);
    return data;
  }

  async fetchSubscriptionBuyLink(
    plan_key: string,
  ): Promise<SubscriptionLinkResponse> {
    const { data } = await api2.get(`/app/end_user/subscription/buy`, {
      params: { plan_key },
    });
    return data;
  }

  async fetchSubscriptionCheckoutSessionStatus(
    sessionId: string,
  ): Promise<SubscriptionCheckoutStatusResponse> {
    const { data } = await api2.get(
      `/app/end_user/subscription/checkout_session_check?session_id=${sessionId}`,
    );
    return data;
  }

  async fetchSubscriptionManageLink(): Promise<SubscriptionLinkResponse> {
    const { data } = await api2.get(`/app/end_user/subscription/manage`);
    return data;
  }

  async fetchVoices(): Promise<Voice[]> {
    const { data } = await ttsApi.get(`/api/v1/list_voices`);
    return data;
  }

  async requestSynthesis({
    text,
    voiceId,
    mediaType = 'mp3', //"pcm16b22k", "wav", "mp3", "flac"
    signal,
  }: RequestSynthesisParams): Promise<string> {
    const { data: requestId } = await ttsApi.post(
      `/api/v1/request_synthesis`,
      {
        text,
        voice_id: voiceId,
        media_type: mediaType,
      },
      { signal },
    );
    return `${ttsApi.defaults.baseURL}/api/v1/synthesize_request/${requestId}`;
  }

  async getCasrModelLinks(): Promise<ModelLinks> {
    const { data } = await api2.get(`/app/end_user/model_links`);
    return data;
  }

  async fetchTechPartners(): Promise<TechPartnersResponse> {
    const { data } = await api2.get('/app/end_user/tech_partners');
    return data;
  }

  async updateConsents({
    accepted_tos,
    accepted_privacy_policy,
    commercial_use_of_pii_consent,
    marketing_consent,
    cookies_consent_types,
  }: UpdateConsentsParams): Promise<Consents> {
    const { data } = await api.post('/app/update_consents', {
      accepted_tos,
      accepted_privacy_policy,
      commercial_use_of_pii_consent,
      marketing_consent,
      cookies_consent_types,
    });
    return data.consents;
  }
}

export const clientApi = new ClientApi();
